import PrimaryButton from "components/buttons/PrimaryButton";
import { AnimatePresence, motion } from "framer-motion";
import Navigation from "../components/navigation/Navigation";

import { Colors } from "components/common/Colors";
import AppStoreButtons from "components/downloadappcta/appstorebuttons/AppStoreButtons";
import Icon from "components/Icons/Icon";
import { t } from "i18n/index";
import { localizeHref, MarketingLangCode } from "i18n/urlLocale";
import { useEffect, useRef, useState } from "react";
import { isMobileOnly } from "react-device-detect";
import FeatureSection from "../components/FeatureSection";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import Footer from "../components/Footer";
import LanguageSwitcher from "../components/LanguageSwitcher";
import OutroCTA from "../components/OutroCTA";
import RelatedFeaturesStrip, { RelatedFeature } from "../components/RelatedFeaturesStrip";
import UspItem from "../components/UspItem";
import "../css/FeaturePage.scss";
import { getFeaturePageTranslations } from "../marketing/i18n/pages/featurePage";

// FeaturePage is a single shared component rendered for six different routes
// (/expense-tracker, /your-map, /social-travel-platform, /travel-journal,
// /travel-reel, /scratch-map). The `type` prop isn't reliably set/unique across
// all six, so we derive a stable i18n namespace key from `pageTitle` instead.
const FEATURE_PAGE_KEYS: Record<string, string> = {
	"Expense Tracker": "expenseTracker",
	"Your map": "yourMap",
	"Stippl Travel Profile": "socialTravelPlatform",
	"Travel Journals": "travelJournal",
	"Travel Reels": "travelReel",
	"Scratch map": "scratchMap",
};

const FeaturePage = ({
	title = "",
	text = "",
	cta = "",
	icon = "",
	feature = "",
	usps = [],
	featureList,
	color = "default",
	bgColor = "",
	type = "default",
	headerImage = [],
	imageAlt = undefined,
	pageTitle,
	textSection = undefined,
	faqItems = undefined,
	relatedFeatures = undefined,
	lang = "en",
}: {
	title?: string;
	text?: string;
	cta?: string;
	icon?: string;
	feature?: string;
	usps?: string[];
	featureList: any[];
	color?: string;
	bgColor?: string;
	type?: string;
	headerImage?: string[];
	imageAlt?: string;
	pageTitle?: string;
	textSection?: { heading: string; paragraphs: string[] };
	faqItems?: { question: string; answer: string }[];
	relatedFeatures?: RelatedFeature[];
	lang?: string;
}) => {
	const translations = getFeaturePageTranslations(lang || "en");
	const variantKey = FEATURE_PAGE_KEYS[pageTitle || ""] || "default";
	const translatedFeatureList = featureList.map((item, index) => ({
		...item,
		subtitle: t(translations, `featurePage.${variantKey}.featureList.${index}.subtitle`, item.subtitle),
		title: t(translations, `featurePage.${variantKey}.featureList.${index}.title`, item.title),
		text: t(translations, `featurePage.${variantKey}.featureList.${index}.text`, item.text),
	}));
	const featureContainer = useRef();
	const [activeFeature, setActiveFeature] = useState(translatedFeatureList[0]);
	// Default to desktop on first render — flip after mount to match the actual
	// device. Avoids hydration mismatches: server has no navigator/userAgent.
	const [isMobile, setIsMobile] = useState(false);
	useEffect(() => {
		setIsMobile(isMobileOnly);
	}, []);
	useEffect(() => {
		if (typeof window === "undefined") return;
		window.scrollTo(0, 0);
	}, [title]);
	return (
		<div className={`feature-page ${bgColor || color}`}>
			<Navigation impact={false} langMenu={<LanguageSwitcher type="page" />} />
			<div className={`feature-header ${type}`}>
				<div className="left-content">
					<div className="content-container">
						{type === "default" && isMobile ? (
							<div className="icon-name">
								<div className={`feature-icon-container ${color}`}>
									<div className="feature-icon">
										<Icon
											//@ts-ignore
											name={icon}
											fill={Colors?.[color]}
										/>
									</div>
								</div>
								<p>{feature}</p>
							</div>
						) : null}
						<h1 className="small">{t(translations, `featurePage.${variantKey}.title`, title)}</h1>
						<p>{t(translations, `featurePage.${variantKey}.text`, text)}</p>
						<ul>
							{usps.map((usp, index) => (
								<UspItem key={index} text={t(translations, `featurePage.${variantKey}.usps.${index}`, usp)} color={color} />
							))}
						</ul>
						<PrimaryButton
							label={t(translations, `featurePage.${variantKey}.cta`, cta)}
							size="xl"
							color={color === "main1" ? "green" : color === "main3" ? "orange" : color === "main4" ? "purple" : color === "main2" ? "magenta" : "black"}
							//@ts-ignore
							onClick={() => featureContainer?.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
						/>
						<AppStoreButtons />
					</div>
				</div>
				<div className="right-content">
					<picture>
						<source media="(max-width:880px)" srcSet={headerImage[1]} />
						{/* <source srcSet={BudgetVisual} type="image/webp" /> */}
						<img loading="lazy" src={headerImage[0]} alt={imageAlt || "Feature visual"} />
					</picture>
				</div>
			</div>
			<div className="container__full-width">
				<div className="container__inner">
					<div className="feature-container">
						{!isMobile ? (
							<>
								<div className="feature-list" ref={featureContainer}>
									{translatedFeatureList.map((feature) => (
										<FeatureSection feature={feature} setActiveFeature={setActiveFeature} />
									))}
								</div>
								<div className="feature-image-container">
									<div className={`image-card ${color}`}>
										{translatedFeatureList.map((feature) => (
											<AnimatePresence>
												{feature?.image === activeFeature.image ? (
													<motion.div
														className="feature-image"
														initial={{ opacity: 0 }}
														animate={{ opacity: 1 }}
														exit={{ opacity: 0 }}
														transition={{ duration: 0.3 }}
													>
														<picture>
															<source srcSet={feature?.image} type="image/webp" />
															<img loading="lazy" src={feature?.image} alt={imageAlt || "Feature visual"} />
														</picture>
													</motion.div>
												) : null}
											</AnimatePresence>
										))}
									</div>
								</div>
							</>
						) : (
							<div className="feature-list">
								{translatedFeatureList.map((feature) => (
									<>
										<div className="feature-image">
											<picture>
												<source srcSet={feature?.image} type="image/webp" />
												<img loading="lazy" src={feature?.image} alt={imageAlt || "Feature visual"} />
											</picture>
										</div>

										<div className="feature-item">
											<div className="content-container">
												<p className="sub-header">{feature.subtitle}</p>
												<h2>{feature.title}</h2>
												<p className="text">{feature.text}</p>
											</div>
										</div>
									</>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
			{(textSection || (faqItems && faqItems.length > 0)) && (
				<div className="container__full-width">
					<div className="container__inner">
						<div className="fp-seo-section">
							{textSection && (
								<div className="fp-text-section">
									<h2>{t(translations, `featurePage.${variantKey}.textSection.heading`, textSection.heading)}</h2>
									{textSection.paragraphs.map((p: string, index: number) => (
										<p key={p}>{t(translations, `featurePage.${variantKey}.textSection.paragraphs.${index}`, p)}</p>
									))}
								</div>
							)}
							{faqItems && faqItems.length > 0 && (
								<div className="fp-faq-section">
									<h2>{t(translations, "featurePage.chrome.faqHeading", "Frequently asked questions")}</h2>
									{faqItems.map((item: { question: string; answer: string }, index: number) => (
										<div key={item.question} className="fp-faq-item">
											<h3>{t(translations, `featurePage.${variantKey}.faqItems.${index}.question`, item.question)}</h3>
											<p>{t(translations, `featurePage.${variantKey}.faqItems.${index}.answer`, item.answer)}</p>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
			{relatedFeatures && relatedFeatures.length > 0 && (
				<div className="container__full-width">
					<div className="container__inner">
						{/* hrefs come from featurePageContent.ts unprefixed (data stays locale-agnostic on
						    purpose, see i18n/urlLocale.ts's doc comment on `localizeHref`) — localised here
						    at the render site, where the visitor's `lang` actually is. */}
						<RelatedFeaturesStrip
							features={relatedFeatures.map((f) => ({ ...f, href: localizeHref(f.href, lang as MarketingLangCode) }))}
						/>
					</div>
				</div>
			)}
			<OutroCTA />
			<Footer />
			<DownloadAppQrCard />
		</div>
	);
};

export default FeaturePage;
